import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { AppStoreQuery, AppStoreService, DetailContext } from '@core/state';
import { clientRoute, issuingRoute, productRoute } from '@shared/shared-routes';
import { Observable, Subscription } from 'rxjs';
import { filter, shareReplay, startWith, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class RouterService {
  private routeChanges: Subscription;

  constructor(private appStoreQuery: AppStoreQuery, private appStoreService: AppStoreService, private router: Router) {}

  public init(): void {
    this.routeChanges = this.router.events
      .pipe(
        startWith(this.router.routerState.snapshot),
        filter((event) => event instanceof NavigationEnd),
        tap((event) => {
          this.appStoreService.updateCurrentRoute((event as NavigationEnd).url.split('/'));
          this.appStoreService.updateCurrentDetailContext(this.getDetailContext((event as NavigationEnd).url));
        }),
        shareReplay(),
      )
      .subscribe();
  }

  public destroy(): void {
    this.routeChanges.unsubscribe();
  }

  public selectCurrentDetailContext(): Observable<DetailContext> {
    return this.appStoreQuery.selectCurrentDetailContext();
  }

  public selectCurrentRoute(): Observable<string[]> {
    return this.appStoreQuery.selectCurrentRoute();
  }

  public get currentRoute(): string[] {
    return this.appStoreQuery.getValue().currentRoute;
  }

  private getDetailContext(url: string): DetailContext {
    const pathOnly = url.split('?')[0];
    // Client investor routes use /client/:productId/... with no literal /product/ segment; product-scoped
    // APIs (data room, etc.) still need DetailContext 'product' so namespaced URL builders resolve correctly.
    const clientProductScoped =
      pathOnly.includes(`/${clientRoute}/`) &&
      /\/client\/[^/]+\/(?:ddq(?:\/|$)|summary(?:\/|$))/.test(pathOnly);
    const clientDashboardDdqProductScoped =
      /\/clients\/[^/]+\/dashboard\/(?:fund-ddq-runs|ddq-invites|marketplace)\/[^/]+\/ddq(?:\/|$)/.test(pathOnly);
    if (clientProductScoped || clientDashboardDdqProductScoped) {
      return 'product';
    }

    const segments = pathOnly.split('/').filter(Boolean);
    // 'issuing-process' is more specific than the outer 'issuing' segment that always precedes
    // it in nested LP routes (e.g. /investor/issuing/:id/issuing-process/:id/data-room), so it
    // must win regardless of scan order.
    if (segments.includes('issuing-process')) {
      return 'issuing-process';
    }
    for (const segment of segments) {
      // App routes use `/product/...`; some legacy URLs and tests use `/products/...`.
      if (segment === productRoute || segment === 'products') {
        return 'product';
      }
      if (segment === issuingRoute) {
        return 'issuing';
      }
    }

    return undefined;
  }
}
